<template>
  <div aria-labelledby="basket-summary-title">
    <div v-if="basketTile || foodPackagingDropdownOptions.length">

      <div v-if="visibleBasketItems.length" class="basket-list">
        <!-- Render basket products -->
        <div v-for="product in visibleBasketItems" :key="product.id">
          <BasketProduct
            :product="product"
            :max="productMaxMap[product.id]"
            :editable="isBasketProductEditable(product)"
            @product-quantity-change="onProductQuantityChange"
          />
        </div>
      </div>

      <div v-else class="empty-basket">
        <p>{{ $t('system.label.emptyBasket') }}</p>
      </div>

      <div v-if="foodPackagingDropdownOptions.length && isTakeawayOrderSelected" class="basket-food-packaging">
        <h4 class="basket-food-packaging__title">{{ $t('orderMenu.label.foodPackaging') }}</h4>
        <Dropdown
          :value="currentSelectedFoodPackagingProductId"
          :label="$t('orderMenu.label.foodPackagingSelect')"
          :options="foodPackagingDropdownOptions"
          :sort="false"
          :required="true"
          :error="foodPackagingErrorMessage"
          @input="onFoodPackagingSelect"
        />
      </div>

      <!-- Payments summary (if available) -->
      <template v-if="basketTile && visibleBasketItems.length && basketTile.paymentsList && basketTile.paymentsList.length">
        <div class="basket-payments">
          <div
            v-for="(payment, index) in basketTile.paymentsList"
            :key="'payment-' + index"
            class="payment-row"
          >
            <span>{{ payment.paymentType }}</span>
            <span>-{{ payment.formattedPaymentAmount }}</span>
          </div>
        </div>
      </template>

      <!-- Total price -->
      <h3 v-if="basketTile && visibleBasketItems.length" class="basket-total normalText mb-0" :class="{'no-vat': !showVat}">
        <span>{{ $t('system.label.total') }}:</span>
        <span>{{ basketTile.formattedTotal }}</span>
      </h3>

      <!-- VAT summary (if available) -->
      <template v-if="basketTile && visibleBasketItems.length && showVat && basketTile.transVats?.vatCodes?.length">
        <div class="basket-vats">
          <div
            v-for="(vat, index) in basketTile.transVats.vatCodes"
            :key="'vat-' + index"
            class="vat-row"
          >
            <span>{{ $t('system.label.vat') }} {{ vat.formattedVatPercent }}</span>
            <span>{{ vat.formattedVatAmount }}</span>
            <span>{{ vat.formattedNetAmount }}</span>
          </div>
        </div>
      </template>
    </div>
    <div v-else class="empty-basket">
      <p>{{ $t('system.label.emptyBasket') }}</p>
    </div>
  </div>
</template>

<script>
import BasketProduct from "@/components/sub-components/Basket/BasketProduct.vue";
import Dropdown from "@/components/forms/Dropdown.vue";
import {useAppStore} from "@/stores/app";
import { Order } from "@/constants/constants";
import { getCanonicalOrderType } from "@/utils/orderUtils";

export default {
  components: {
    BasketProduct,
    Dropdown,
  },
  props: {
    editable: {
      type: Boolean,
      default: true
    },
    productId: Number,
    basketTile: {
      type: Object,
      required: false,
      default: null
    },
    showVat: {
      type: Boolean,
      default: true
    },
    productMaxMap: {
      type: Object,
      required: false,
      default: () => ({})
    },
    foodPackagingProducts: {
      type: Array,
      required: false,
      default: () => []
    },
    foodPackagingMandatoryError: {
      type: Boolean,
      default: false,
    }
  },
  setup() {
    const appStore = useAppStore();

    return {
      appStore,
    };
  },
  computed: {
    basketItems() {
      return this.basketTile?.basketItems || [];
    },
    visibleBasketItems() {
      return this.basketItems.filter(product => !this.isFoodPackagingBasketItem(product));
    },
    isTakeawayOrderSelected() {
      return getCanonicalOrderType(this.appStore?.currentOrderMenuOrderType) === Order.type.TAKEAWAY;
    },
    foodPackagingProductIds() {
      return new Set(
        this.foodPackagingProducts
          .map(product => Number(product?.id))
          .filter(productId => !Number.isNaN(productId))
      );
    },
    foodPackagingDropdownOptions() {
      return this.foodPackagingProducts
        .filter(product => product?.id != null)
        .map(product => ({
          value: String(product.id),
          label: product.formattedPrice
            ? `${product.name} (${product.formattedPrice})`
            : product.name,
        }));
    },
    currentSelectedFoodPackagingProductId() {
      const selectedFoodPackagingItem = this.basketItems.find(product => this.isFoodPackagingBasketItem(product));
      return selectedFoodPackagingItem ? String(selectedFoodPackagingItem.prodId) : '';
    },
    foodPackagingErrorMessage() {
      if (!this.foodPackagingMandatoryError) {
        return '';
      }

      return this.$t('orderMenu.messages.error.foodPackagingRequired');
    },
  },
  methods: {
    close() {
      this.$emit('close');
    },
    isFoodPackagingBasketItem(product) {
      return this.foodPackagingProductIds.has(Number(product?.prodId));
    },
    isBasketProductEditable(product) {
      return this.editable && !this.isFoodPackagingBasketItem(product);
    },
    onProductQuantityChange(basketId, quantity) {
      this.$emit('product-quantity-change', basketId, quantity);
    },
    onFoodPackagingSelect(productId) {
      const normalizedProductId = Number(productId);

      if (!normalizedProductId) {
        return;
      }

      this.$emit('food-packaging-select', normalizedProductId);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/css/customizer';

.basket-container {
  margin: 0 auto;
  padding: 0 10px;
  width: 100%;
}

.basket-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.basket-food-packaging {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #ddd;
}

.basket-food-packaging__title {
  margin: 0 0 12px;
  text-align: left;
  font-size: 16px;
  font-weight: bold;
}

.basket-total {
  display: flex;
  justify-content: space-between;
  font-weight: bold;
  padding: 12px 0;
  border-top: 1px solid;
  margin-top: 12px;
}

.basket-total.no-vat {
  border-top: none;
  margin-top: 0;
}

.basket-vats {
  margin-top: 12px;
  font-size: 16px;
}

.vat-row,
.vat-total {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.basket-payments {
  margin-top: 12px;
  padding: 0 12px;
  font-size: 16px;
}

.payment-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
}

.empty-basket {
  text-align: center;
  padding: 20px;
  color: #666;
}
</style>
